import { useCallback, useMemo, useState } from "react";
import { AgentSkillCategory } from "@common/types/agentSkill";
import { OrganizationAction } from "@common/userOrganizationRoles";
import { TablePageHeader } from "@components/pageHeader/TablePageHeader";
import { Button } from "@design-system/Button/Button";
import { WithHotkey } from "@design-system/Button/WithHotkey";
import { HStack } from "@design-system/HStack/HStack";
import { Picker } from "@design-system/Picker/Picker.lib";
import type { PickerItemData } from "@design-system/Picker/Picker.types";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { ColumnSettings } from "@design-system/ViewToolbar/ColumnSettings";
import { useCanPerformOrganizationAction } from "@hooks/permissions/organization/useCanPerformOrganizationAction";
import { useStateAwareNavigate } from "@hooks/useStateAwareNavigate";
import { useCreateAgentSkillMutation } from "@api/agentSkill.api";
import { useOrganizationClients } from "@components/organizationView/activity/useOrganizationClients";
import {
    AGENT_SKILL_CATEGORY_LABELS,
    AGENT_SKILL_MANAGE_FORBIDDEN_TOOLTIP,
    FIRM_SCOPE_LABEL,
} from "./agentSkillCopy";
import { AgentSkillEditorDrawer } from "./AgentSkillEditorDrawer";
import {
    AgentSkillFormValues,
    buildCreateAgentSkillRequest,
    emptyAgentSkillForm,
} from "./agentSkillForm";
import { AgentSkillCallbacks, AgentSkillsList } from "./AgentSkillsList";
import {
    AgentSkillsFilters,
    DEFAULT_AGENT_SKILLS_FILTERS,
    EnabledFilter,
} from "./agentSkillsFilters";
import { defaultAgentSkillsView } from "./agentSkillsView";
import { agentSkillsViewConfig } from "./agentSkillsViewConfig";
import { useAgentSkillLifecycle } from "./useAgentSkillLifecycle";
import { useAgentSkillsView } from "./useAgentSkillsView";

const FIRM_SCOPE = "firm";

const CATEGORY_ITEMS: Array<PickerItemData<AgentSkillCategory | "all">> = [
    { value: "all", label: "All categories" },
    ...Object.values(AgentSkillCategory).map((value) => ({
        value,
        label: AGENT_SKILL_CATEGORY_LABELS[value],
    })),
];

const ENABLED_ITEMS: Array<PickerItemData<EnabledFilter>> = [
    { value: "all", label: "All" },
    { value: "enabled", label: "Enabled" },
    { value: "disabled", label: "Disabled" },
];

export const agentSkillPath = (skillId: string) =>
    `/automation/skills/${skillId}`;

export const AgentSkillsPage = () => {
    const navigate = useStateAwareNavigate();
    const { toast } = useToaster();
    const [scope, setScope] = useState<string>(FIRM_SCOPE);
    const [filters, setFilters] = useState<AgentSkillsFilters>(
        DEFAULT_AGENT_SKILLS_FILTERS,
    );
    const [isCreateOpen, setIsCreateOpen] = useState(false);
    const canManage = useCanPerformOrganizationAction(
        OrganizationAction.MANAGE_AGENT_SKILLS,
    );
    const { view, handleViewChange } = useAgentSkillsView();
    const createSkill = useCreateAgentSkillMutation();
    const {
        enable,
        disable,
        requestDelete,
        requestReset,
        deleteModal,
        resetModal,
    } = useAgentSkillLifecycle();
    const { data: workspaces } = useOrganizationClients();

    const clientWorkspaceId = scope === FIRM_SCOPE ? null : scope;
    const clientName =
        workspaces?.find((workspace) => String(workspace.id) === scope)?.name ??
        null;

    const scopeItems = useMemo<Array<PickerItemData<string>>>(
        () => [
            { value: FIRM_SCOPE, label: FIRM_SCOPE_LABEL },
            ...(workspaces ?? []).map((workspace) => ({
                value: String(workspace.id),
                label: workspace.name ?? "Unnamed client",
            })),
        ],
        [workspaces],
    );

    const callbacks = useMemo<AgentSkillCallbacks>(
        () => ({
            onOpen: (skill) => navigate(agentSkillPath(skill.id)),
            onMenuItemSelect: ({ key, skill }) => {
                const target = { skillId: skill.id, title: skill.title };
                switch (key) {
                    case "open":
                        navigate(agentSkillPath(skill.id));
                        break;
                    case "editDetails":
                        navigate(agentSkillPath(skill.id), {
                            state: { editSkill: true },
                        });
                        break;
                    case "enable":
                        void enable(target);
                        break;
                    case "disable":
                        void disable(target);
                        break;
                    case "delete":
                        requestDelete(target);
                        break;
                    case "reset":
                        requestReset(target);
                        break;
                }
            },
        }),
        [navigate, enable, disable, requestDelete, requestReset],
    );

    const handleCreate = useCallback(
        async (values: AgentSkillFormValues) => {
            const created = await createSkill.mutateAsync(
                buildCreateAgentSkillRequest(values),
            );
            toast(`Created skill "${created.title}"`);
        },
        [createSkill, toast],
    );

    return (
        <main>
            <TablePageHeader
                search={filters.search}
                onSearchChange={(search) =>
                    setFilters((current) => ({ ...current, search }))
                }
                isFullPage
            >
                <HStack gap={8} className="ml-auto">
                    <Picker.Select
                        items={scopeItems}
                        value={scope}
                        onValueChange={(value) => setScope(value ?? FIRM_SCOPE)}
                        searchable
                    />
                    <Picker.Select
                        items={CATEGORY_ITEMS}
                        value={filters.category ?? "all"}
                        onValueChange={(value) =>
                            setFilters((current) => ({
                                ...current,
                                category:
                                    !value || value === "all" ? null : value,
                            }))
                        }
                    />
                    <Picker.Select
                        items={ENABLED_ITEMS}
                        value={filters.enabled}
                        onValueChange={(value) =>
                            setFilters((current) => ({
                                ...current,
                                enabled: value ?? "all",
                            }))
                        }
                    />
                    <ColumnSettings
                        view={view}
                        viewConfig={agentSkillsViewConfig}
                        defaultView={defaultAgentSkillsView}
                        onViewChange={handleViewChange}
                    />
                    <WithHotkey
                        hotKey="alt+n"
                        hotKeyDisplayPosition={canManage ? "tooltip" : "none"}
                        hotKeyOptions={{
                            description: "Create skill",
                            preventDefault: true,
                        }}
                        tooltip={
                            canManage
                                ? undefined
                                : AGENT_SKILL_MANAGE_FORBIDDEN_TOOLTIP
                        }
                    >
                        <Button
                            variant="primary"
                            size="md"
                            disabled={!canManage}
                            onClick={() => setIsCreateOpen(true)}
                        >
                            Create skill
                        </Button>
                    </WithHotkey>
                </HStack>
            </TablePageHeader>

            <AgentSkillsList
                clientWorkspaceId={clientWorkspaceId}
                filters={filters}
                view={view}
                canManage={canManage}
                callbacks={callbacks}
                onViewChange={handleViewChange}
                onClearFilters={() => setFilters(DEFAULT_AGENT_SKILLS_FILTERS)}
                onCreate={() => setIsCreateOpen(true)}
            />

            {isCreateOpen && (
                <AgentSkillEditorDrawer
                    title="Create skill"
                    submitLabel="Create"
                    initialValues={emptyAgentSkillForm(clientWorkspaceId)}
                    clientName={clientName}
                    onSubmit={handleCreate}
                    onClose={() => setIsCreateOpen(false)}
                />
            )}

            {deleteModal}
            {resetModal}
        </main>
    );
};
